/**
 * "Training" section of the redesigned homepage (landing-home-v2), shown in
 * place of LandingMission: the processes Lev is trained on, topic chips that
 * can start the hero chat (via `onTopicSelect`), plus founder cards
 * (TRAINING_FOUNDERS; entries without a photo render a gray placeholder).
 */
"use client";

import Image from "next/image";
import {
  BadgeDollarSign,
  Megaphone,
  Palette,
  PieChart,
  Tag,
  UsersRound,
  type LucideIcon,
} from "lucide-react";
import { AnimatedSection } from "@/components/atoms/AnimatedSection";
import { cn } from "@/lib/utils";

export type LandingTrainingTopicId =
  | "customer_validation"
  | "market_sizing"
  | "pricing"
  | "branding"
  | "go_to_market"
  | "fundraising";

export interface LandingTrainingTopic {
  id: LandingTrainingTopicId;
  label: string;
  icon: LucideIcon;
  /** Tailwind classes for the icon square's background + icon color. */
  tone: string;
  /** Sent verbatim as a complete first chat message, so it must stand on its own. */
  starter: string;
}

interface TrainingFounder {
  name: string;
  title: string;
  /** Optional third caption line, e.g. their role at Lev. */
  role?: string;
  /** Public path to a black-and-white portrait; omitted renders a gray placeholder. */
  photoSrc?: string;
}

const TRAINING_TOPICS: readonly LandingTrainingTopic[] = [
  {
    id: "customer_validation",
    label: "Customer validation",
    icon: UsersRound,
    tone: "bg-amber-100 text-amber-700",
    starter: "I want to validate that customers really have the problem I'm solving.",
  },
  {
    id: "market_sizing",
    label: "Market sizing",
    icon: PieChart,
    tone: "bg-teal-100 text-teal-700",
    starter: "I want to figure out how big the market is for my startup idea.",
  },
  {
    id: "pricing",
    label: "Pricing",
    icon: Tag,
    tone: "bg-lev-orange-100 text-lev-orange-700",
    starter: "I want help figuring out how to price my product.",
  },
  {
    id: "branding",
    label: "Branding",
    icon: Palette,
    tone: "bg-violet-100 text-violet-700",
    starter: "I want help building a brand for my startup.",
  },
  {
    id: "go_to_market",
    label: "Go to market",
    icon: Megaphone,
    tone: "bg-rose-100 text-rose-700",
    starter: "I want to plan how to take my product to market.",
  },
  {
    id: "fundraising",
    label: "Fundraising",
    icon: BadgeDollarSign,
    tone: "bg-emerald-100 text-emerald-700",
    starter: "I'm getting ready to raise and want help preparing.",
  },
];

const TRAINING_FOUNDERS: readonly TrainingFounder[] = [
  {
    name: "T. A. McCann",
    title: "6x Founder, Venture Partner",
    role: "Lev CEO",
    photoSrc: "/landing/founders/ta-mccann.png",
  },
  {
    name: "Jay Bartot",
    title: "4x Founder",
    role: "Lev CTO",
    photoSrc: "/landing/founders/jay-bartot.png",
  },
];

function FounderCard({ founder, className }: { founder: TrainingFounder; className?: string }) {
  return (
    <figure className={cn("w-full max-w-[240px] rounded-2xl bg-white p-2.5 shadow-sm", className)}>
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-xl bg-landing-bg-medium">
        {founder.photoSrc && (
          <Image
            src={founder.photoSrc}
            alt={founder.name}
            fill
            sizes="240px"
            className="object-cover grayscale"
          />
        )}
      </div>
      <figcaption className="px-1.5 pb-1 pt-3">
        <p className="text-sm font-medium text-landing-dark">{founder.name}</p>
        <p className="text-xs text-landing-secondary">{founder.title}</p>
        {founder.role && <p className="text-xs text-landing-secondary">{founder.role}</p>}
      </figcaption>
    </figure>
  );
}

export interface LandingTrainingProps {
  /** When set, topic chips are buttons that hand their starter to the caller; otherwise static labels. */
  onTopicSelect?: (topic: LandingTrainingTopic) => void;
}

const TOPIC_CHIP_CLASS =
  "flex items-center gap-2 rounded-lg bg-white px-2.5 py-1.5 text-sm text-landing-dark shadow-[0_1px_2px_rgba(45,49,57,0.06)]";

export function LandingTraining({ onTopicSelect }: LandingTrainingProps = {}) {
  return (
    <section className="bg-landing-bg-light flex justify-center pt-12 md:pt-14 pb-16 md:pb-20 px-4">
      <div className="grid w-full max-w-[1024px] grid-cols-1 items-center gap-12 md:grid-cols-2 md:gap-16">
        <div>
          <AnimatedSection delay={0}>
            <div className="bg-landing-bg-medium inline-block px-2.5 py-2 rounded-md mb-8">
              <span className="font-mono text-sm text-landing-secondary uppercase">A new way to build</span>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <h2 className="text-[28px] md:text-[36px] font-normal text-landing-dark leading-[1.3] tracking-tight">
              Lev is trained on the process that built &amp; raised funds for 3,000+ ideas &amp;
              200+ companies.
            </h2>
          </AnimatedSection>
          <AnimatedSection delay={0.2}>
            <p className="mt-8 text-base text-landing-secondary">
              Lev can help across the startup process including:
            </p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {TRAINING_TOPICS.map((topic) => {
                const { id, label, icon: Icon, tone } = topic;
                const content = (
                  <>
                    <span
                      className={cn("flex h-5 w-5 items-center justify-center rounded-md", tone)}
                    >
                      <Icon className="h-3 w-3" aria-hidden="true" />
                    </span>
                    {label}
                  </>
                );
                return onTopicSelect ? (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => onTopicSelect(topic)}
                      className={cn(
                        TOPIC_CHIP_CLASS,
                        "transition-shadow hover:shadow-[0_2px_8px_rgba(45,49,57,0.12)]",
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-dark/20",
                      )}
                    >
                      {content}
                    </button>
                  </li>
                ) : (
                  <li key={id} className={TOPIC_CHIP_CLASS}>
                    {content}
                  </li>
                );
              })}
            </ul>
          </AnimatedSection>
        </div>

        <AnimatedSection delay={0.2} direction="fade">
          <div className="flex items-start justify-center gap-4">
            {TRAINING_FOUNDERS.map((founder, index) => (
              <FounderCard
                key={`${founder.name}-${index}`}
                founder={founder}
                className={index % 2 === 1 ? "mt-8 md:mt-12" : undefined}
              />
            ))}
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}
